// Admin console: every bucket across all tenants, with the schedule that runs
// it unattended and its snapshot history. "Only scheduled" is the triage
// filter — those are the buckets that act on their own and therefore the ones
// that can quietly stop working.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { adminApi } from '../api/adminClient';
import type { AdminBucket } from '../api/types';
import { usePaginated } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatDateTime, formatNumber } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { LoadMore } from '../components/Pagination';
import { Badge, Card } from '../components/ui';
import { IconArchive, IconSearch } from '../components/Icons';

const PAGE = 30;

/** "daily 02:00" style summary; "—" when the job is off. */
function scheduleLabel(freq: string, next: string | undefined, lang: string, t: (k: string) => string): React.ReactNode {
  if (!freq || freq === 'off') return <span className="muted">{t('admin.buckets.off')}</span>;
  const overdue = next ? new Date(next).getTime() < Date.now() : false;
  return (
    <span className="row" style={{ gap: 6 }}>
      <Badge tone={overdue ? 'warning' : 'info'}>{freq}</Badge>
      {next ? <span className={overdue ? '' : 'muted'}>{formatDateTime(next, lang as never)}</span> : null}
    </span>
  );
}

export function AdminBuckets() {
  const { t } = useTranslation();
  const lang = useLang();
  const [q, setQ] = useState('');
  const [scheduledOnly, setScheduledOnly] = useState(false);
  const list = usePaginated(
    (cursor) => adminApi.adminBuckets(PAGE, cursor, q || undefined, scheduledOnly),
    [q, scheduledOnly],
  );

  return (
    <>
      <PageHeader title={t('admin.buckets.title')} subtitle={t('admin.buckets.subtitle')} />

      <Card>
        <div className="row" style={{ padding: '12px 16px', gap: 16, flexWrap: 'wrap' }}>
          <span className="input" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 220 }}>
            <IconSearch width={16} height={16} />
            <input
              style={{ border: 'none', outline: 'none', flex: 1, background: 'transparent' }}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('admin.buckets.filterPlaceholder')}
              aria-label={t('admin.buckets.filterPlaceholder')}
            />
          </span>
          <label className="row" style={{ gap: 8 }}>
            <input type="checkbox" checked={scheduledOnly} onChange={(e) => setScheduledOnly(e.target.checked)} />
            {t('admin.buckets.scheduledOnly')}
          </label>
        </div>

        <ApiState
          status={list.status}
          error={list.error}
          data={list.status === 'error' ? null : list.items}
          onRetry={list.reload}
        >
          {(items: AdminBucket[]) =>
            items.length === 0 ? (
              <EmptyState
                title={scheduledOnly ? t('admin.buckets.emptyScheduled') : t('admin.buckets.empty')}
                icon={<IconArchive width={26} height={26} />}
              />
            ) : (
              <>
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>{t('admin.buckets.colBucket')}</th>
                        <th>{t('admin.buckets.colCustomer')}</th>
                        <th className="num">{t('archives.colObjects')}</th>
                        <th className="num">{t('archives.colSize')}</th>
                        <th>{t('admin.buckets.colSync')}</th>
                        <th>{t('admin.buckets.colCommit')}</th>
                        <th className="num">{t('admin.buckets.colSnapshots')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((b) => (
                        <tr key={b.archiveId}>
                          <td className="cell-strong">{b.name}</td>
                          <td>
                            <Link to={`/customers/${encodeURIComponent(b.tenantId)}`}>{b.tenantName}</Link>
                          </td>
                          <td className="num">{formatNumber(b.objects, lang)}</td>
                          <td className="num">{formatBytes(b.bytes, lang)}</td>
                          <td>{scheduleLabel(b.syncFreq, b.syncNext, lang, t)}</td>
                          <td>{scheduleLabel(b.commitFreq, b.commitNext, lang, t)}</td>
                          <td className="num">
                            {formatNumber(b.snapshots, lang)}
                            {b.lastSnapshotAt ? (
                              <>
                                <br />
                                <span className="muted" style={{ fontSize: 12 }}>
                                  {formatDateTime(b.lastSnapshotAt, lang)}
                                </span>
                              </>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onLoadMore={list.loadMore} />
              </>
            )
          }
        </ApiState>
      </Card>
    </>
  );
}
